import { useAuth } from "@ssf/auth-components";
import {
  getClubTeamsByClubIds,
  getMember,
  getMyTournamentGroups,
  type MyTournamentGroupResponse,
  OrgType,
  RoleType,
} from "@ssf/memdb-api";
import type { TournamentRoom } from "@ssf/play-common";
import { useQuery } from "@tanstack/react-query";
import { formatInTimeZone, zonedTimeToUtc } from "date-fns-tz";
import { useMemo } from "react";

const tournamentTimeZone = "Europe/Stockholm";

const uniqueGroups = <T extends { id: number }>(groups: T[]) => [
  ...new Map(groups.map((group) => [group.id, group])).values(),
];

export const useMemberTournamentGroups = (
  tournamentRooms: [string, TournamentRoom][],
  memberId: number | undefined,
) => {
  const roomGroupIds = useMemo(
    () => tournamentRooms.map(([, room]) => room.memdbGroupId),
    [tournamentRooms],
  );

  const memberGroupsQuery = useQuery({
    queryKey: ["memberTournamentGroups", memberId, roomGroupIds],
    enabled: memberId !== undefined && roomGroupIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    queryFn: async () => {
      const groups = await getMyTournamentGroups({ online: true });
      const roomGroupIdSet = new Set(roomGroupIds);

      return groups.filter(({ id }) => roomGroupIdSet.has(id));
    },
  });
  const managedGroupsQuery = useManagedTournamentGroups();
  const isError = memberGroupsQuery.isError || managedGroupsQuery.isError;
  const isPending =
    memberId === undefined ||
    (roomGroupIds.length > 0 && memberGroupsQuery.isPending) ||
    managedGroupsQuery.isPending;
  const status = isError ? "error" : isPending ? "pending" : "success";
  const data = useMemo(
    () =>
      status === "success"
        ? uniqueGroups([
            ...(managedGroupsQuery.data ?? []),
            ...(memberGroupsQuery.data ?? []),
          ]).filter(({ id }) => roomGroupIds.includes(id))
        : undefined,
    [status, memberGroupsQuery.data, managedGroupsQuery.data, roomGroupIds],
  );

  return {
    data,
    status,
    isLoading: status === "pending" && memberId !== undefined,
    isError,
    error: memberGroupsQuery.error ?? managedGroupsQuery.error,
  };
};

const tournamentParticipationSchema = (fromDate: string) => ({
  group: {
    id: 1,
    name: 1,
    start: 1,
    end: 1,
    arenaStart: 1,
    arenaEnd: 1,
    pairingsystem: 1,
    class: { tournament: { id: 1, online: 1 } },
  },
  filter: `(&(group.class.tournament.online=1)(group.end>=${fromDate}))`,
});

const getManagedTournamentGroups = async (fromDate: string) => {
  const { roles } = await getMember("me", {
    roles: [
      {
        orgid: 1,
        orgtype: 1,
        orgrole: 1,
        seclevel: 1,
        end: 1,
        team: { participations: [tournamentParticipationSchema(fromDate)] },
        filter: `(&(end=1970-01-01)(|(&(orgtype=${OrgType.Club})(|(orgrole=${RoleType.Admin})(orgrole=${RoleType.ContactPerson})))(&(orgtype=${OrgType.Team})(orgrole=${RoleType.TeamLeader1}))))`,
      },
    ],
  });
  const clubIds = roles.flatMap(({ orgtype, orgid }) =>
    orgtype === OrgType.Club ? [orgid] : [],
  );
  const clubTeams = clubIds.length
    ? await getClubTeamsByClubIds({
        clubIds: [...new Set(clubIds)],
        endDate: fromDate,
        tournamentTypes: [],
        schema: [
          {
            participations: [tournamentParticipationSchema(fromDate)],
          },
        ],
      })
    : [];
  const teamLeaderGroups = roles.flatMap((role) =>
    role.orgtype === OrgType.Team
      ? role.team.participations
          .map(({ group }) => group)
          .filter(
            ({ class: tournamentClass }) =>
              role.seclevel === -1 ||
              tournamentClass.tournament.id === role.seclevel,
          )
      : [],
  );
  const clubGroups = clubTeams.flatMap(({ participations }) =>
    participations.map(({ group }) => group),
  );

  return uniqueGroups([...teamLeaderGroups, ...clubGroups]).map((group) => ({
    ...group,
    timeDiff: getArenaTimeDiff(group),
  }));
};

export const useManagedTournamentGroups = () => {
  const { user } = useAuth();
  const fromDate = formatInTimeZone(
    new Date(),
    tournamentTimeZone,
    "yyyy-MM-dd",
  );

  return useQuery({
    queryKey: ["managedTournamentGroups", user?.profile.sub, fromDate],
    queryFn: () => getManagedTournamentGroups(fromDate),
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });
};

const safeParseTime = (dateStr?: string): number | undefined => {
  if (!dateStr) {
    return undefined;
  }
  const time = zonedTimeToUtc(dateStr, tournamentTimeZone).getTime();
  return Number.isNaN(time) ? undefined : time;
};

export const getStartTime = (
  group: MyTournamentGroupResponse,
): number | undefined =>
  group.arenaStart
    ? (safeParseTime(`${group.start}T${group.arenaStart}`) ??
      safeParseTime(group.start))
    : safeParseTime(group.start);

const getEndTime = (group: MyTournamentGroupResponse) =>
  group.end
    ? (safeParseTime(`${group.end}T${group.arenaEnd ?? "23:59"}`) ??
      safeParseTime(group.end))
    : undefined;

const getArenaTimeDiff = (group: MyTournamentGroupResponse) => {
  const now = Date.now();
  const startTime = getStartTime(group);
  const endTime = getEndTime(group);
  if (startTime !== undefined && now < startTime) return startTime - now;
  if (endTime !== undefined && now > endTime) return endTime - now;
  return 0;
};

export const hasNotEnded = (group: MyTournamentGroupResponse) => {
  const endTime = getEndTime(group);
  return endTime === undefined || endTime >= Date.now();
};

export const sortByStartTime = (
  groupA: MyTournamentGroupResponse,
  groupB: MyTournamentGroupResponse,
) => {
  const aTime = getStartTime(groupA);
  const bTime = getStartTime(groupB);
  if (aTime === undefined && bTime === undefined) return 0;
  if (aTime === undefined) return 1;
  if (bTime === undefined) return -1;
  return aTime - bTime;
};
